import axios from 'axios'

const origin = process.env.VUE_APP_API_ORIGIN

const get = path => axios.get(origin + path).then(response => response.data)
const post = (path, payload) => axios.post(origin + path, payload).then(response => response.data)

const PROVIDER_ROLES = [
  'PLATFORM_SUPER_ADMIN',
  'PLATFORM_OPERATIONS_ADMIN',
  'PLATFORM_SUPPORT_VIEWER'
]

function roleValues (context) {
  if (context && Array.isArray(context.roles)) return context.roles
  if (context && Array.isArray(context.ROLES)) return context.ROLES
  return []
}

/**
 * 既存のPlatformContractManagementは初期表示時だけ
 * PLATFORM_SUPER_ADMINの有無を確認している。
 *
 * 実際のルート認可・操作可否はグローバルaccess-contextと
 * accessPolicy / accessManagementUiPolicyを正本にするため、このServiceから
 * 当該Componentへ返す応答にだけ互換Roleを追加する。グローバルキャッシュや
 * バックエンドへは書き戻さず、Operations / SupportをBreak-glass扱いしない。
 */
export function providerComponentContext (data) {
  const context = data && typeof data === 'object' ? data : {}
  const roles = roleValues(context)
    .map(role => String(role || '').trim())
    .filter(Boolean)
  const providerRoles = roles.filter(role => PROVIDER_ROLES.includes(role))

  if (!providerRoles.length || roles.includes('PLATFORM_SUPER_ADMIN')) {
    return context
  }

  return {
    ...context,
    roles: Array.from(new Set([...roles, 'PLATFORM_SUPER_ADMIN'])),
    actualProviderRoles: providerRoles
  }
}

export default {
  getAccessContext () {
    return get('/access-context').then(providerComponentContext)
  },
  getAccounts () {
    return get('/access-management/accounts')
  },
  getPlans () {
    return get('/access-management/plans')
  },
  createAccount (payload) {
    return post('/access-management/accounts', payload)
  },
  /*
   * 契約に関わる会社。**テナントの取引先（クライアント）とは別のもの。**
   *
   *   CONTRACTOR … 契約企業（署名して払う会社）
   *   BILL_TO    … 請求先。空なら契約企業へ請求する
   *   RESELLER   … PREIKA側の販売代理店
   *   AGENT      … お客様側の代行者（社労士・親会社）
   *
   * 保存は**送った区分だけ**を入れ替える。送らなかった区分は触らない——
   * 「代理店だけ直したい」ときに契約企業まで消えては困る。
   */
  getContractParties (accountId) {
    return get(`/access-management/accounts/${accountId}/contract-parties`)
  },
  /*
   * 代理店へ払う手数料の条件。**いまは条件を持つだけ。**
   * 計算も支払もしない（PREIKA運営側に金額が1円も無い）。
   *
   * 保存すると、前の条件は**消さずに閉じる**。いつからいくらだったかが
   * 後から言えないと困る。
   */
  getContractCommission (accountId) {
    return get(`/access-management/accounts/${accountId}/contract-commission`)
  },
  replaceContractCommission (payload) {
    return post('/access-management/contract-commission', payload)
  },
  /**
   * 初期特別価格（0100）と契約の開始日。契約が無ければ null。
   * 「最初の N か月は 基本料 ○円・1人あたり ○円」。0円なら無料。
   */
  getContractIntroPrice (accountId) {
    return get(`/access-management/accounts/${accountId}/contract-intro-price`)
  },
  /** 置き換える。intro_months が空（null）なら「やめる」。前の条件は消さずに閉じる。 */
  replaceContractIntroPrice (payload) {
    return post('/access-management/contract-intro-price', payload)
  },
  /**
   * 最低月額の、契約ごとの設定。
   * { accountId, mode: 'PLAN'|'NONE'|'CUSTOM', customAmount, remarks,
   *   planMinimumMonthlyAmount（今月の請求に使うプランの、今月1日時点の料金の記録の最低月額。プランも料金も月次確定と同じ選び方で、月の途中でプランを替えた月は月初のプラン。監査 E-3）,
   *   introPriceActive（今月が初期特別価格の期間か） }。
   * 設定の行が無ければ mode は 'PLAN'（プランの最低月額に従う）。
   * 読むのは CONTRACT_VIEW、保存は CONTRACT_UPDATE（初期特別価格と同じ）。
   */
  getContractMinimumCharge (accountId) {
    return get(`/access-management/accounts/${accountId}/contract-minimum-charge`)
  },
  /** 置き換える。{ accountId, mode, customAmount, remarks }。前の設定は消さずに閉じる。 */
  replaceContractMinimumCharge (payload) {
    return post('/access-management/contract-minimum-charge', payload)
  },
  /**
   * ボリュームディスカウント（人数帯ごとの割引率）の、契約ごとの設定。
   * { accountId, mode: 'PLAN'|'NONE'|'CUSTOM', tiers（CUSTOM の帯 [{ fromSeat, discountRate }]。ほかは []）,
   *   remarks, updatedUser, updatedAt,
   *   planTiers（今月の請求に使うプランの、今月1日時点の帯。無ければ []）,
   *   planPricePerUser, planBasePrice（同じプランの今月1日時点の料金。試算に使う。無ければ null）,
   *   introPriceActive（今月が初期特別価格の期間か） }。契約が無ければ null。
   * 設定の行が無ければ mode は 'PLAN'。読むのは CONTRACT_VIEW、保存は CONTRACT_UPDATE（最低月額と同じ）。
   */
  getContractVolumeDiscount (accountId) {
    return get(`/access-management/accounts/${accountId}/contract-volume-discount`)
  },
  /** 置き換える。{ accountId, mode, tiers, remarks }。前の設定は消さずに閉じる。 */
  replaceContractVolumeDiscount (payload) {
    return post('/access-management/contract-volume-discount', payload)
  },
  replaceContractParties (payload) {
    return post('/access-management/contract-parties', payload)
  },
  getOwnerInvitation (accountId) {
    return get(`/access-management/accounts/${accountId}/owner-invitation`)
  },
  resendOwnerInvitation (accountId) {
    return post(`/access-management/accounts/${accountId}/owner-invitation/resend`, {})
  },
  replaceOwnerInvitation (accountId, payload) {
    return post(`/access-management/accounts/${accountId}/owner-invitation/replace`, payload)
  },
  revokeOwnerInvitation (accountId) {
    return post(`/access-management/accounts/${accountId}/owner-invitation/revoke`, {})
  },
  updateContract (payload) {
    return post('/access-management/contract', payload)
  },
  getFeatures (accountId) {
    return get(`/access-management/accounts/${accountId}/features`)
  },
  /**
   * その会社のお支払い方法。
   * { accountId, billingExempt（請求の対象外の会社＝請求書の発行元か）,
   *   allowedMethods（[{ method: 'INVOICE'|'CARD', allowed }]。運営がこの会社に許可しているか）,
   *   cardPaymentAvailable（カード払いが使える環境か。false のあいだカードの行は押せない）,
   *   currentMethod（お客様の請求に使っている方法）,
   *   card（登録済みのカード { status, brand, last4, expMonth, expYear }。無ければ null） }。
   * 読むのは CONTRACT_VIEW（「利用可能機能」を読むのと同じ）。
   */
  getPaymentMethods (accountId) {
    return get(`/access-management/accounts/${accountId}/payment-methods`)
  },
  /**
   * その会社に許可するお支払い方法を保存する（2026-10-15）。
   * payload は { invoiceAllowed, cardAllowed } のうち、**変えた行だけ**（入れない＝変えない）。
   * 会社は道で名指しする（本文に会社の番号を入れない）。
   * back は中で「利用可能機能」の切り替え（updateFeature）と同じ書き込みを通る。
   * 保存できるのは FEATURE_MANAGE（「利用可能機能」の切り替えと同じ）。
   */
  savePaymentMethods (accountId, payload) {
    return post(`/access-management/accounts/${accountId}/payment-methods`, payload)
  },
  updateFeature (payload) {
    return post('/access-management/feature', payload)
  },
  getRoles (accountId) {
    return get(`/access-management/accounts/${accountId}/roles`)
  },
  getPermissions () {
    return get('/access-management/permissions')
  },
  createRole (payload) {
    return post('/access-management/role', payload)
  },
  replaceRolePermissions (payload) {
    return post('/access-management/role/permissions', payload)
  },
  getUsers (accountId) {
    return get(`/access-management/accounts/${accountId}/users`)
  },

  /**
   * いま生きている一時パスがあるか（場面 S-95）。
   *
   * **出す前に必ず読む。** 返ってきた livePassId を、そのまま発行の
   * 要求へ添える。添えないと、2人の運営が同時に出したときに**あとから
   * 通ったほうが、先に渡した1本を黙って殺す。**
   *
   * **enabled も返る。** 閉じているときに「出す」を見せると、押しても
   * 必ず断られるボタンになる。「機能はOFF」なのに壊れた機能に見える。
   *
   * **文字列そのものは返ってこない。** DBにも入っていない。
   */
  /**
   * 一時パスの入口が開いているかだけを聞く（場面 S-95）。
   *
   * **対象者が決まる前に読む。** 画面は誰を対象にするか決まる前に、
   * 入口を出すかどうかを決めないといけない。対象者を受け取る口でしか
   * 読めないと、**入口が出ないと押せない、押さないと入口が出ない**
   * という輪になる。実際そうなっていた。
   *
   * **会社も利用者も送らない。** 返るのは enabled ひとつだけ。
   */
  getTemporaryPassStatus () {
    return get('/access-management/temporary-recovery-pass/status')
  },

  getTemporaryPassState (accountId, userId) {
    return get(`/access-management/accounts/${accountId}/users/${userId}/temporary-recovery-pass`)
  },

  /**
   * 一時パスを出す（場面 S-95）。
   *
   * **出せるのは PREIKA運営だけ。** 顧客企業の管理者には出させない。
   * 出せると、会社側の管理者が自分の第2要素を迂回する資格情報を
   * 自分で作れる。
   *
   * **返る文字列は、ここでしか出てこない。** 取り直しはできない。
   * 画面はこれを1度だけ出し、運営が電話で読み上げる。
   * **メールには入らない。**
   */
  issueTemporaryPass (accountId, userId, payload) {
    return post(`/access-management/accounts/${accountId}/users/${userId}/temporary-recovery-pass`, payload)
  },
  assignRole (payload) {
    return post('/access-management/user-role/assign', payload)
  },
  removeRole (payload) {
    return post('/access-management/user-role/remove', payload)
  },
  getAuditLogs (accountId) {
    return get(`/access-management/accounts/${accountId}/audit-logs`)
  }
}
